import React, { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { getAnalysis, videoStreamUrl, deleteAnalysis, api } from "@/lib/api";
import VideoTimeline from "@/components/VideoTimeline";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft, Spinner, Warning, CheckCircle, Pulse, ShieldCheck, Lightning,
  ArrowClockwise, Trash, Download, Person, Printer, SlackLogo, EyeSlash,
} from "@phosphor-icons/react";

const RISK_STYLES = {
  low:      { label: "LOW",      tone: "text-hazard-safe",     bg: "bg-hazard-safe",     ring: "border-hazard-safe" },
  medium:   { label: "MEDIUM",   tone: "text-hazard-warning",  bg: "bg-hazard-warning",  ring: "border-hazard-warning" },
  high:     { label: "HIGH",     tone: "text-hazard-critical", bg: "bg-hazard-critical", ring: "border-hazard-critical" },
  critical: { label: "CRITICAL", tone: "text-hazard-critical", bg: "bg-hazard-critical", ring: "border-hazard-critical" },
};

const SEV_STYLES = {
  low: { dot: "bg-hazard-safe", text: "text-hazard-safe", icon: <CheckCircle size={16} weight="bold" /> },
  medium: { dot: "bg-hazard-warning", text: "text-hazard-warning", icon: <Pulse size={16} weight="bold" /> },
  high: { dot: "bg-hazard-critical", text: "text-hazard-critical", icon: <Warning size={16} weight="bold" /> },
  critical: { dot: "bg-hazard-critical", text: "text-hazard-critical", icon: <Warning size={16} weight="bold" /> },
};

export default function AnalysisDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);
  const timelineRef = useRef(null);

  const seekTo = (t) => {
    if (timelineRef.current && Number.isFinite(Number(t))) {
      timelineRef.current.seekTo(Number(t));
    }
  };

  const load = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await getAnalysis(id);
      setA(data);
      return data;
    } catch (e) {
      toast.error("Could not load analysis");
      nav("/dashboard");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Load video as blob so cookies are sent reliably
  useEffect(() => {
    let revoked = null;
    (async () => {
      try {
        const r = await api.get(`/analyses/${id}/video`, { responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        revoked = url;
        setVideoUrl(url);
      } catch (e) {
        // silent; show placeholder
      }
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [id]);

  useEffect(() => {
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (a?.status === "processing") {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => load(true), 5000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [a?.status]); // eslint-disable-line

  const onDelete = async () => {
    if (!window.confirm("Delete this analysis?")) return;
    await deleteAnalysis(id);
    toast.success("Deleted");
    nav("/dashboard");
  };

  if (loading || !a) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar variant="app" />
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10 font-mono text-xs uppercase tracking-[0.3em] text-ink-500">Loading analysis…</div>
      </div>
    );
  }

  const r = a.result;
  const rs = r ? RISK_STYLES[r.risk_level] || RISK_STYLES.medium : null;
  const hazards = Array.isArray(r?.hazards) ? r.hazards : [];
  const bodyImpact = Array.isArray(r?.body_impact) ? r.body_impact : [];
  const recommendations = Array.isArray(r?.recommendations) ? r.recommendations : [];

  return (
    <div className="min-h-screen bg-white">
      <Navbar variant="app" />
      <Toaster richColors position="top-right" />

      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-8">
        {/* Breadcrumb / actions */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-ink-900" data-testid="back-to-dashboard">
            <ArrowLeft size={14} weight="bold" /> Back to dashboard
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="export-pdf" className="rounded-none border-ink-100 hover:border-ink-900">
              <Printer size={14} className="mr-1" weight="bold" /> Export PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => load()} data-testid="reload" className="rounded-none border-ink-100 hover:border-ink-900">
              <ArrowClockwise size={14} className="mr-1" weight="bold" /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete} data-testid="delete" className="rounded-none text-ink-500 hover:text-hazard-critical">
              <Trash size={14} weight="bold" />
            </Button>
          </div>
        </div>

        {/* Title row */}
        <div className="mt-4 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-1">Case · {a.id.slice(0, 8).toUpperCase()}</div>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-ink-900">{a.title}</h1>
            <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500">
              {a.industry} · {a.task_type} · {a.duration_minutes}min/shift · {new Date(a.created_at).toLocaleString()}
            </div>
          </div>
          {rs && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="risk-badge-row">
              <div className={`px-4 py-3 border ${rs.ring} flex items-center gap-4`} data-testid="risk-badge">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Overall risk</div>
                <div className={`font-display font-black text-4xl tracking-tighter ${rs.tone}`}>{r.overall_risk_score}<span className="text-ink-500 text-base font-mono">/100</span></div>
                <div className={`px-2 py-1 ${rs.bg} text-white font-mono text-[10px] uppercase tracking-[0.25em]`}>{rs.label}</div>
              </div>
              {a.slack_alert?.posted_at && (
                <div
                  data-testid="slack-alert-badge"
                  className={`inline-flex items-center gap-2 px-3 py-2 border ${a.slack_alert.success ? "border-hazard-safe bg-hazard-safe/5" : "border-hazard-warning bg-hazard-warning/5"}`}
                  title={a.slack_alert.success ? "Slack notification delivered" : (a.slack_alert.error || "Slack delivery failed")}
                >
                  <SlackLogo size={14} weight="bold" className={a.slack_alert.success ? "text-hazard-safe" : "text-hazard-warning"} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-700">
                    {a.slack_alert.success ? "Posted to Slack" : "Slack failed"} · {formatPostedAt(a.slack_alert.posted_at)}
                  </span>
                </div>
              )}
              {a.face_blur === true && (
                <div
                  data-testid="face-blur-badge"
                  className="inline-flex items-center gap-2 px-3 py-2 border border-hazard-safe bg-hazard-safe/5"
                  title="Faces automatically anonymized before storage and analysis"
                >
                  <EyeSlash size={14} weight="bold" className="text-hazard-safe" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-700">Faces blurred</span>
                </div>
              )}
              {a.face_blur === false && a.face_blur_requested === true && (
                <div
                  data-testid="face-blur-failed-badge"
                  className="inline-flex items-center gap-2 px-3 py-2 border border-hazard-warning bg-hazard-warning/5"
                  title={a.face_blur_error || "Face blur was requested but failed — raw video stored"}
                >
                  <Warning size={14} weight="bold" className="text-hazard-warning" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-700">Blur failed</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Video + Status */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <VideoTimeline
              ref={timelineRef}
              videoUrl={videoUrl}
              hazards={hazards}
              contentType={a.content_type}
              sizeBytes={a.size_bytes}
            />
          </div>

          <div className="lg:col-span-5 space-y-6">
            {a.status === "processing" && (
              <div className="border border-safety bg-safety/5 p-6">
                <div className="flex items-center gap-2 text-safety font-mono text-[10px] uppercase tracking-[0.3em]">
                  <Spinner size={14} weight="bold" className="animate-spin" /> Analyzing
                </div>
                <div className="mt-3 font-display font-bold text-2xl text-ink-900">Gemini is reviewing the clip…</div>
                <p className="mt-2 text-ink-700">The page will refresh automatically when the report is ready.</p>
                <div className="mt-4 h-1 bg-ink-100 overflow-hidden">
                  <div className="h-full w-1/3 bg-safety animate-scan" />
                </div>
              </div>
            )}

            {a.status === "failed" && (
              <div className="border border-hazard-critical bg-hazard-critical/5 p-6">
                <div className="flex items-center gap-2 text-hazard-critical font-mono text-[10px] uppercase tracking-[0.3em]">
                  <Warning size={14} weight="bold" /> Analysis failed
                </div>
                <div className="mt-3 font-display font-bold text-2xl text-ink-900">We couldn't analyze this clip.</div>
                <p className="mt-2 text-ink-700 break-words">{a.error || "Please try a shorter or higher-quality clip."}</p>
              </div>
            )}

            {a.status === "complete" && r && (
              <>
                <SectionCard title="Executive summary" icon={<Lightning size={18} weight="bold" />}>
                  <p className="text-ink-900 leading-relaxed">{r.summary}</p>
                </SectionCard>

                <SectionCard title="Likelihood" icon={<Person size={18} weight="bold" />}>
                  <p className="text-ink-700 leading-relaxed">{r.estimated_injury_likelihood}</p>
                </SectionCard>
              </>
            )}
          </div>
        </div>

        {/* Hazards + Body Impact */}
        {a.status === "complete" && r && (
          <>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-7 border border-ink-100">
                <SectionHeader>Identified hazards · {hazards.length}</SectionHeader>
                <div className="divide-y divide-ink-100">
                  {hazards.map((h, i) => {
                    const s = SEV_STYLES[h.severity] || SEV_STYLES.medium;
                    const hasTs = h.start_seconds !== null && h.start_seconds !== undefined && Number.isFinite(Number(h.start_seconds));
                    return (
                      <button
                        type="button"
                        key={i}
                        onClick={() => hasTs && seekTo(h.start_seconds)}
                        disabled={!hasTs}
                        className={`w-full text-left p-5 ${hasTs ? "hover:bg-ink-50 cursor-pointer" : "cursor-default"} transition-colors`}
                        data-testid={`hazard-${i}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`mt-1 w-2 h-2 ${s.dot}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-3">
                              <div className="font-display font-bold text-lg text-ink-900">{h.name}</div>
                              <div className={`inline-flex items-center gap-1 ${s.text} font-mono text-[10px] uppercase tracking-[0.25em]`}>{s.icon} {h.severity}</div>
                            </div>
                            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500">
                              {h.body_region} {h.frequency ? `· ${h.frequency}` : ""}
                              {hasTs && (
                                <span className="ml-2 text-safety">
                                  · {formatTimestamp(h.start_seconds)} ↗ play
                                </span>
                              )}
                            </div>
                            <p className="mt-2 text-ink-700 leading-relaxed">{h.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="lg:col-span-5 border border-ink-100">
                <SectionHeader>Body region impact</SectionHeader>
                <div className="p-6 space-y-4">
                  {bodyImpact.map((b) => (
                    <div key={b.region}>
                      <div className="flex items-center justify-between text-sm">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-700">{b.region.replace(/_/g, " ")}</div>
                        <div className="font-display font-bold text-ink-900">{b.severity}</div>
                      </div>
                      <div className="mt-2 h-2 bg-ink-100">
                        <div
                          className={`h-full ${b.severity >= 70 ? "bg-hazard-critical" : b.severity >= 40 ? "bg-hazard-warning" : "bg-hazard-safe"}`}
                          style={{ width: `${Math.min(100, Math.max(0, b.severity))}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recommendations + Safer technique */}
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-7 border border-ink-100">
                <SectionHeader>Recommendations · {recommendations.length}</SectionHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-ink-100">
                  {recommendations.map((rec, i) => (
                    <div key={i} className="p-5" data-testid={`rec-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-safety">PRIORITY · {rec.priority}</div>
                        <div className="w-5 h-5 bg-ink-900 text-white text-[10px] flex items-center justify-center font-mono">{i + 1}</div>
                      </div>
                      <div className="font-display font-bold text-lg text-ink-900">{rec.title}</div>
                      <p className="mt-1 text-ink-700 text-sm leading-relaxed">{rec.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-5 border-2 border-safety bg-safety/5 p-6">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-safety">
                  <ShieldCheck size={14} weight="bold" /> Safer technique
                </div>
                <div className="mt-3 font-display font-bold text-2xl text-ink-900 tracking-tight">The way to do this same task — safely.</div>
                <p className="mt-3 text-ink-900 leading-relaxed">{r.safer_technique}</p>
                <Button
                  className="mt-6 rounded-none bg-ink-900 hover:bg-safety text-white"
                  data-testid="copy-technique"
                  onClick={() => { navigator.clipboard.writeText(r.safer_technique); toast.success("Copied to clipboard"); }}
                >
                  <Download size={14} className="mr-2" weight="bold" /> Copy for huddle
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div className="px-5 py-3 border-b border-ink-100 bg-ink-50 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-700">
      {children}
    </div>
  );
}

function SectionCard({ title, icon, children }) {
  return (
    <div className="border border-ink-100">
      <SectionHeader>
        <span className="inline-flex items-center gap-2">{icon}{title}</span>
      </SectionHeader>
      <div className="p-5">{children}</div>
    </div>
  );
}

function formatTimestamp(s) {
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return "0:00";
  const m = Math.floor(n / 60);
  const sec = Math.floor(n % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

function formatPostedAt(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return sameDay ? time : `${d.toLocaleDateString()} ${time}`;
  } catch (e) { return "—"; }
}
